import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CurrencyContext = createContext();

// Currency info for formatting
const CURRENCY_INFO = {
  GBP: { symbol: '£', name: 'British Pound', decimals: 2 },
  USD: { symbol: '$', name: 'US Dollar', decimals: 2 },
  EUR: { symbol: '€', name: 'Euro', decimals: 2 },
  JPY: { symbol: '¥', name: 'Japanese Yen', decimals: 0 },
  CNY: { symbol: '¥', name: 'Chinese Yuan', decimals: 2 },
  INR: { symbol: '₹', name: 'Indian Rupee', decimals: 2 },
  AUD: { symbol: 'A$', name: 'Australian Dollar', decimals: 2 },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', decimals: 2 },
  CHF: { symbol: 'CHF ', name: 'Swiss Franc', decimals: 2 },
  KRW: { symbol: '₩', name: 'South Korean Won', decimals: 0 },
  MXN: { symbol: 'MX$', name: 'Mexican Peso', decimals: 2 },
  BRL: { symbol: 'R$', name: 'Brazilian Real', decimals: 2 },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', decimals: 2 },
  HKD: { symbol: 'HK$', name: 'Hong Kong Dollar', decimals: 2 },
  SEK: { symbol: 'kr ', name: 'Swedish Krona', decimals: 2 },
  NOK: { symbol: 'kr ', name: 'Norwegian Krone', decimals: 2 },
  DKK: { symbol: 'kr ', name: 'Danish Krone', decimals: 2 },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', decimals: 2 },
  ZAR: { symbol: 'R', name: 'South African Rand', decimals: 2 },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', decimals: 2 },
};

// Detect currency from browser locale
function detectCurrencyFromLocale() {
  const locale = navigator.language || navigator.userLanguage || 'en-GB';
  
  // Map locales to currencies
  const localeMapping = {
    'en-GB': 'GBP',
    'en-US': 'USD',
    'en-AU': 'AUD',
    'en-CA': 'CAD',
    'en-NZ': 'NZD',
    'en-ZA': 'ZAR',
    'en-SG': 'SGD',
    'en-IN': 'INR',
    'de': 'EUR',
    'de-DE': 'EUR',
    'de-AT': 'EUR',
    'de-CH': 'CHF',
    'fr': 'EUR',
    'fr-FR': 'EUR',
    'fr-CA': 'CAD',
    'fr-CH': 'CHF',
    'es': 'EUR',
    'es-ES': 'EUR',
    'es-MX': 'MXN',
    'it': 'EUR',
    'it-IT': 'EUR',
    'nl': 'EUR',
    'nl-NL': 'EUR',
    'pt': 'EUR',
    'pt-PT': 'EUR',
    'pt-BR': 'BRL',
    'ja': 'JPY',
    'ja-JP': 'JPY',
    'zh': 'CNY',
    'zh-CN': 'CNY',
    'zh-HK': 'HKD',
    'zh-TW': 'TWD',
    'ko': 'KRW',
    'ko-KR': 'KRW',
    'hi': 'INR',
    'hi-IN': 'INR',
    'ar': 'AED',
    'ar-AE': 'AED',
    'sv': 'SEK',
    'sv-SE': 'SEK',
    'nb': 'NOK',
    'nb-NO': 'NOK',
    'da': 'DKK',
    'da-DK': 'DKK',
  };
  
  // Try exact match
  if (localeMapping[locale]) {
    return localeMapping[locale];
  }
  
  // Try language-only match
  const lang = locale.split('-')[0];
  if (localeMapping[lang]) {
    return localeMapping[lang];
  }
  
  // Default to GBP (London-first platform)
  return 'GBP';
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    // Check localStorage first, then detect from browser
    const saved = localStorage.getItem('mealhoster_currency');
    return saved || detectCurrencyFromLocale();
  });
  
  const [rates, setRates] = useState({ GBP: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch exchange rates
  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      const locale = navigator.language || 'en-GB';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/currency/rates?locale=${encodeURIComponent(locale)}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch exchange rates');
      }
      
      const data = await response.json();
      setRates(data.rates || { GBP: 1 });
      setError(null);
    } catch (err) {
      console.error('Currency fetch error:', err);
      setError(err.message);
      // Use fallback rates
      setRates({
        GBP: 1,
        USD: 1.27,
        EUR: 1.17,
        JPY: 190,
        INR: 106,
        AUD: 1.93,
        CAD: 1.72,
      });
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch rates on mount and refresh every hour
  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 60 * 60 * 1000); // 1 hour
    return () => clearInterval(interval);
  }, [fetchRates]);
  
  // Save currency preference
  useEffect(() => {
    localStorage.setItem('mealhoster_currency', currency);
  }, [currency]);
  
  // Convert price from GBP to selected currency
  const convertPrice = useCallback((priceInGBP) => {
    if (!priceInGBP || isNaN(priceInGBP)) return 0;
    const rate = rates[currency] || 1;
    return priceInGBP * rate;
  }, [currency, rates]);
  
  // Format price with currency symbol
  const formatPrice = useCallback((priceInGBP, options = {}) => {
    const converted = convertPrice(priceInGBP);
    const info = CURRENCY_INFO[currency] || CURRENCY_INFO.GBP;
    const decimals = options.decimals ?? info.decimals;
    
    if (decimals === 0) {
      return `${info.symbol}${Math.round(converted).toLocaleString()}`;
    }
    
    return `${info.symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }, [currency, convertPrice]);
  
  // Get just the currency symbol
  const getCurrencySymbol = useCallback(() => {
    return CURRENCY_INFO[currency]?.symbol || '£';
  }, [currency]);
  
  // Get available currencies
  const getAvailableCurrencies = useCallback(() => {
    return Object.keys(rates).filter(code => CURRENCY_INFO[code]).map(code => ({
      code,
      ...CURRENCY_INFO[code],
    }));
  }, [rates]);
  
  const value = {
    currency,
    setCurrency,
    rates,
    loading,
    error,
    convertPrice,
    formatPrice,
    getCurrencySymbol,
    getAvailableCurrencies,
    currencyInfo: CURRENCY_INFO[currency] || CURRENCY_INFO.GBP,
    refreshRates: fetchRates,
  };
  
  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

// Convenience component for displaying prices
export function Price({ amount, className = '', showOriginal = false }) {
  const { formatPrice, currency } = useCurrency();
  
  if (amount === null || amount === undefined) {
    return null;
  }
  
  return (
    <span className={className}>
      {formatPrice(amount)}
      {showOriginal && currency !== 'GBP' && (
        <span className="text-xs text-gray-500 ml-1">(£{amount.toFixed(2)})</span>
      )}
    </span>
  );
}

export default CurrencyContext;
